import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaAngleLeft } from 'react-icons/fa6';

import { fetchOrderById, askQuestion as submitOrderQuestion, requestInstallation, requestW9Info as submitW9InfoRequest } from '../api/orders';
import { getToken } from '../utils/auth';

import PortalSkeleton from '../components/other/PortalSkeleton';
import Error from '../components/other/Error';
import RequestPortalLink, { isExpiredPortalTokenError } from '../components/other/RequestPortalLink';
import OrderHeader from '../components/other/OrderHeader';
import AddressBlock from '../components/other/AddressBlock';
import LineItem from '../components/other/LineItem';
import ModalMain from '../components/modals/ModalMain';
import ModalAction from '../components/modals/ModalAction';
import ModalField from '../components/modals/ModalField';
import { openPortalPdf, openGorgiasChat, pushDataLayerEvent, trackKlaviyoEvent, klavityoPortalItems } from '../../utils/utils';
import { formatPhoneNumber, useFormatUSD } from '../../utils/formatting';

import { modalConfig } from '../utils/modalFields';
import { sortRestrictedLineItemsLast } from '../../utils/constants';
// import { SHIPPING_YOUTUBE_VIDEO_ID } from '../../utils/constants';
import {
  usePortalPdfQueryParams,
  openAchPdf,
  openDeliveryCheatSheetPdf,
} from '../hooks/usePortalPdfQueryParams';

import { PdfIcon } from '../../utils/icons';
import POUploader, { isGovernmentPoOrder, hasPurchaseOrderFile } from '../components/POUploader';
import { InstallForm } from '../../install_portal/install_paperwork/components/InstallForm';

/** Set true to skip Gorgias and always open the ask-question modal (business-hours logic lives in openGorgiasChat). */
const ASK_QUESTION_MODAL_ONLY = false;

/**
 * Detail view for a placed order.
 *
 * Read-only: unlike DraftOrderDetailPage there are no approve / decline /
 * request-update / quantity-edit affordances, because none of those apply once
 * an order exists. What this adds instead is fulfillment state, per-line-item
 * estimated ship dates, and the balance outstanding.
 *
 * Class names deliberately mirror DraftOrderDetailPage (`draft-order
 * detail-view`, `draft-order-customer`, `customer-name`, `customer-address`)
 * so this page inherits the existing stylesheet instead of needing its own.
 */



/** Analytics + API wiring for order-portal modals (ask question vs installation quote). */
const ORDER_PORTAL_MODAL_FLOW = {
  askQuestion: {
    gorgiasOpenEvent: 'order_portal_ask_question_gorgias_opened',
    modalOpenEvent: 'order_portal_ask_question_modal_open',
    submitEvent: 'order_portal_ask_question',
    klaviyoEvent: 'Order Portal Question Asked',
    successTitle: 'Question Submitted',
    successMessage:
      'Your question has been submitted. Our team will review and get back to you shortly.',
    submit: submitOrderQuestion,
    errorMessage: 'submit your question',
  },
  requestInstallation: {
    modalOpenEvent: 'order_portal_request_installation_modal_open',
    submitEvent: 'order_portal_request_installation',
    klaviyoEvent: 'Order Portal Installation Quote Requested',
    successTitle: 'Request Submitted',
    successMessage:
      'Your installation quote request has been submitted. Our team will review and get back to you shortly.',
    submit: requestInstallation,
    errorMessage: 'submit your installation quote request',
  },
  requestW9Info: {
    gorgiasOpenEvent: 'order_portal_request_w9_info_gorgias_opened',
    modalOpenEvent: 'order_portal_request_w9_info_modal_open',
    submitEvent: 'order_portal_request_w9_info',
    klaviyoEvent: 'Order Portal W9 Info Requested',
    successTitle: 'Request Submitted',
    successMessage:
      'Your W9 request has been submitted. Our team will review and get back to you shortly.',
    submit: submitW9InfoRequest,
    errorMessage: 'submit your W9 request',
  },
};



// const openYoutubeModal = () => {
//   showModal(
//     <div
//       title="Video"
//       className="youtube-modal"
//     >
//       <div className="modal-youtube">
//         <iframe
//           src={`https://www.youtube-nocookie.com/embed/${YT_VIDEO_ID}?rel=0`}
//           title="YouTube video"
//           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//           allowFullScreen
//         />
//       </div>
//     </div>
//   );
// };


/**
 * Shared order-portal modal form — uses modalConfig fields and orders API submitters.
 * `title` / `className` are read by ModalMain from the direct child props.
 */
function OrderPortalActionModal({ orderId, modalType, title, className, onSuccess }) {
  const config = modalConfig[modalType];
  const fields = config?.fields || [];
  const flow = ORDER_PORTAL_MODAL_FLOW[modalType];

  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  

  const allRequiredFilled = fields.every((field) => {
    if (!field.required) return true;
    const val = formData[field.name];
    return val != null && String(val).trim() !== '';
  });

  async function handleSubmit() {
    const errors = {};
    fields.forEach((field) => {
      if (!field.required) return;
      const val = formData[field.name];
      if (val == null || !String(val).trim()) {
        errors[field.name] = 'This field is required';
      }
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const body = String(formData.text ?? '').trim();
    setIsProcessing(true);
    const { token } = getToken();

    try {
      await flow.submit(orderId, body, token);
      onSuccess?.();
    } catch (err) {
      console.error(`Order portal modal submit failed (${modalType}):`, err);
      alert(`Failed to ${flow.errorMessage}: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <div className="modal-inner-wrapper">
      {config?.description && (
        <div
          className="modal-description"
          dangerouslySetInnerHTML={{ __html: config.description }}
        />
      )}

      <div className="modal-fields">
        {fields.map((field) => (
          <ModalField
            key={field.name}
            field={field}
            value={formData[field.name]}
            onChange={(name, value) => {
              setFormData((prev) => ({ ...prev, [name]: value }));
              setFormErrors((prev) => {
                if (!prev[name]) return prev;
                const { [name]: _, ...rest } = prev;
                return rest;
              });
            }}
            error={formErrors[field.name]}
          />
        ))}
      </div>

      <div className="modal-actions">
        <button
          type="button"
          className={`button ${config?.buttonClass || 'button--primary button--bordered'}`}
          onClick={handleSubmit}
          disabled={isProcessing || !allRequiredFilled}
        >
          {isProcessing ? 'Processing...' : config?.primaryButton}
        </button>
      </div>
    </div>
  );
}




function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAttributes, setExpandedAttributes] = useState(new Set());
  const [modalContent, setModalContent] = useState(null);

  const hasTrackedViewRef = useRef(null);

  const loadOrder = useCallback(async () => {
    const { token } = getToken();

    if (!token) {
      setError('No authentication token found. Please use the link from your email.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await fetchOrderById(orderId, token);
      setOrder(data.order || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    if (!order || hasTrackedViewRef.current === order.id) return;
    hasTrackedViewRef.current = order.id;

    const customer = order.customer;
    const identifyPayload = {
      ...(customer?.email && { $email: customer.email }),
      ...(customer?.firstName && { $first_name: customer.firstName }),
      ...(customer?.lastName && { $last_name: customer.lastName }),
      ...(customer?.phone && { $phone_number: customer.phone }),
    };
    if (Object.keys(identifyPayload).length > 0) {
      const learnq = window._learnq || [];
      learnq.push(['identify', identifyPayload]);
    }

    trackKlaviyoEvent('Order Portal Viewed', {
      $value: Number(order.currentTotalPrice),
      customer_properties: identifyPayload,
      OrderId: order.id,
      OrderName: order.name,
      ItemNames: order.lineItems?.map((item) => item.title),
      Brands: order.lineItems?.map((item) => item.vendor),
      Items: JSON.stringify(klavityoPortalItems(order.lineItems || [])),
    });

    pushDataLayerEvent('order_portal_detail_view', {
      order_id: order.id,
      order_name: order.name,
      customer_email: customer?.email,
      order_value: Number(order.currentTotalPrice),
    });
  }, [order]);

  usePortalPdfQueryParams({
    enabled: !!order && !!orderId,
    dedupeId: orderId,
    actions: {
      // ?open_chat=1: chat prefilled with the order; falls back to the question form.
      // (No open_approve here: orders are already approved.)
      open_chat: () => handleAskQuestionGorgiasClick(),
      open_pdf: () => {
        if (order?.invoicePdf) {
          openPortalPdf(order.invoicePdf, `Invoice ${order.name}`);
        }
      },
      open_ach: () => openAchPdf({
        order_id: order?.id,
        order_name: order?.name,
        customer_email: order?.customer?.email,
      }),
      open_delivery: () => openDeliveryCheatSheetPdf({
        order_id: order?.id,
        order_name: order?.name,
        customer_email: order?.customer?.email,
      }),
    },
  });

  const toggleAttributesExpanded = (index) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const showModal = (content) => {
    setModalContent(content);
  };

  const closeModal = () => {
    setModalContent(null);
  };

  const openOrderPortalModal = (modalType) => {
    if (!order) return;

    const config = modalConfig[modalType];
    const flow = ORDER_PORTAL_MODAL_FLOW[modalType];
    const id = order.id;
    const customerEmail = order.customer?.email;
    const orderName = order.name;

    const analyticsPayload = {
      order_id: id,
      customer_email: customerEmail,
      order_name: orderName,
      modal_type: modalType,
    };

    pushDataLayerEvent(flow.modalOpenEvent, analyticsPayload);

    showModal(
      <OrderPortalActionModal
        orderId={id}
        modalType={modalType}
        title={config.title}
        className={config.className}
        onSuccess={() => {
          pushDataLayerEvent(flow.submitEvent, analyticsPayload);

          trackKlaviyoEvent(flow.klaviyoEvent, {
            OrderId: id,
            OrderName: orderName,
            customer_email: customerEmail,
            Source: 'order_portal_modal',
            ModalType: modalType,
          });

          showModal(
            <ModalAction
              mode="success"
              title={flow.successTitle}
              message={flow.successMessage}
            />,
          );
        }}
      />,
    );
  };

  const handleAskQuestionClick = () => openOrderPortalModal('askQuestion');

  const handleAskQuestionGorgiasClick = () => {
    if (!order) return;

    if (ASK_QUESTION_MODAL_ONLY) {
      handleAskQuestionClick();
      return;
    }

    const modalType = 'askQuestion';
    const flow = ORDER_PORTAL_MODAL_FLOW[modalType];
    const id = order.id;
    const customerEmail = order.customer?.email;
    const orderName = order.name;

    const analyticsPayload = {
      order_id: id,
      customer_email: customerEmail,
      order_name: orderName,
      modal_type: modalType,
    };

    const prefill = `I have a question about ${order.name}`;
    openGorgiasChat(prefill).then((opened) => {
      if (!opened) {
        // After hours, widget missing, or chat unavailable — fall back to the form modal.
        handleAskQuestionClick();
        return;
      }

      pushDataLayerEvent(flow.gorgiasOpenEvent, analyticsPayload);

      trackKlaviyoEvent(flow.klaviyoEvent, {
        OrderId: id,
        OrderName: orderName,
        customer_email: customerEmail,
        Source: 'gorgias_opened',
        ModalType: modalType,
      });
    });
  };

  const handleRequestW9InfoModal = () => openOrderPortalModal('requestW9Info');
  const handleRequestW9InfoClick = () => {
    if (!order) return;

    const modalType = 'requestW9Info';
    const flow = ORDER_PORTAL_MODAL_FLOW[modalType];
    const id = order.id;
    const customerEmail = order.customer?.email;
    const orderName = order.name;

    const analyticsPayload = {
      order_id: id,
      customer_email: customerEmail,
      order_name: orderName,
      modal_type: modalType,
    };
    // console.log('analyticsPayload', analyticsPayload);

    const prefill = `I need to request W9 information for ${order?.name}`;
    openGorgiasChat(prefill).then((opened) => {
      if (!opened) {
        handleRequestW9InfoModal();
        return;
      }
    });
    
    pushDataLayerEvent(flow.gorgiasOpenEvent, analyticsPayload);
  };

  const handleRequestInstallationClick = () => openOrderPortalModal('requestInstallation');

  const handleUploadPoClick = () => {
    if (!order) return;
    showModal(
      <POUploader
        orderId={order.id}
        showCard={false}
        showTitle={false}
        title="Upload Purchase Order"
        className="po-upload-modal"
        message="Upload your purchase order here. We’ll attach it to this order."
        onUploaded={() => {
          loadOrder();
        }}
      />,
    );
  };

  const openAchInfoPdf = () => {
    if (!order) return;
    openAchPdf({
      order_id: order.id,
      order_name: order.name,
      customer_email: order.customer?.email,
      source: 'order_detail_link',
    });
  };




  
  if (loading) return <PortalSkeleton />;

  if (error && isExpiredPortalTokenError(error)) {
    return <RequestPortalLink expiredToken={getToken().token} />;
  }

  if (error) return <Error message={error} onRetry={loadOrder} title="Unable to Load Order" />;
  if (!order) {
    return <Error message="Order not found." onRetry={loadOrder} title="Unable to Load Order" />;
  }

  const lineItems = sortRestrictedLineItemsLast(order.lineItems);
  const hasDiscount = parseFloat(order.currentTotalDiscounts) > 0;
  const outstanding = parseFloat(order.totalOutstanding);
  const hasOutstanding = Number.isFinite(outstanding) && outstanding > 0;

  const subtotal =
    (parseFloat(order.currentSubtotalPrice) || 0) + (parseFloat(order.currentTotalDiscounts) || 0);

  return (
    <main className="order-detail">
      <div className="back-button-container">
        <button type="button" className="back-button" onClick={() => navigate('/orders')}>
          <FaAngleLeft />
          &nbsp;Back to Orders
        </button>
      </div>

      <div className="draft-order detail-view order-detail-view" data-order-number={order.name}>
        <OrderHeader order={order} />

        <div className="draft-order-header draft-order-customer">
          {order.customer && (
            <div className="customer-name">
              <div className="customer-name-inner">
                <strong>Customer:</strong>
                <p>
                  {[order.customer.firstName, order.customer.lastName].filter(Boolean).join(' ')}
                </p>

                {order.customer.email && (
                  <p className="customer-email">{order.customer.email}</p>
                )}

                {order.customer.phone && (
                  <p className="customer-phone">{formatPhoneNumber(order.customer.phone)}</p>
                )}

                {/* {order.taxExempt ? (
                  <span className="pill-button pill-button-tax-exempt">Tax Exempt</span>
                ) : null}

                {order.statusPageUrl && (
                  <p>
                    <a href={order.statusPageUrl} target="_blank" rel="noopener noreferrer">
                      View Order Status
                    </a>
                  </p>
                )} */}

                {order.invoicePdf && (
                  <p className="order-invoice-link">
                    <a
                      href={order.invoicePdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        openPortalPdf(order.invoicePdf, `Invoice ${order.name}`);
                      }}
                    >
                      View Invoice <PdfIcon strokeWidth={4} />
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}

          <AddressBlock label="Shipping Address:" address={order.shippingAddress} />
          <AddressBlock label="Billing Address:" address={order.billingAddress} />
        </div>

        <div className="line-items line-items--order-detail">
          
          {lineItems.map((item, idx) => (
            <LineItem
              key={item.id || idx}
              item={item}
              index={idx}
              expandThreshold={5}
              isExpanded={expandedAttributes.has(idx)}
              onToggleExpand={toggleAttributesExpanded}
              variant="detail"
              isOrder
              estimatedShipDate={item.estimatedShipDate}
              // unfulfilledQuantity={item.unfulfilledQuantity}
              showManual
            />
          ))}
        </div>


        <div className="total-wrapper">
          <div className="order-summary">
            <div className="summary-row">
              <span className="strong">Subtotal</span>
              <span className="summary-amount-value">
                {useFormatUSD(subtotal)}
              </span>
            </div>

            {hasDiscount && (
              <div className="summary-row">
                <span className="strong">Discount</span>
                <span className="summary-amount-value">
                  -{useFormatUSD(order.currentTotalDiscounts)}
                </span>
              </div>
            )}

            {parseFloat(order.totalShippingPrice) > 0 && (
              <div className="summary-row">
                <span className="strong">{order.totalShippingName || 'Shipping'}</span>
                <span className="summary-amount-value">
                  {useFormatUSD(order.totalShippingPrice)}
                </span>
              </div>
            )}

            {parseFloat(order.currentTotalTax) > 0 && (
              <div className="summary-row">
                <span className="strong">Tax</span>
                <span className="summary-amount-value">
                  {useFormatUSD(order.currentTotalTax)}
                </span>
              </div>
            )}

            <div className="summary-row total">
              <span className="strong">Total</span>
              <span className="summary-amount-value">
                {useFormatUSD(order.currentTotalPrice)}
              </span>
            </div>

            {parseFloat(order.totalReceived) > 0 && (
              <div className="summary-row">
                <span className="strong">Paid</span>
                <span className="summary-amount-value">
                  {useFormatUSD(order.totalReceived)}
                </span>
              </div>
            )}

            {hasOutstanding && (
              <div className="summary-row total">
                <span className="strong">Balance Due</span>
                <span className="summary-amount-value">
                  {useFormatUSD(order.totalOutstanding)}
                </span>
              </div>
            )}
          </div>
        </div>



        <div className={`order-actions order-actions--bottom`}>

        
            <button
              type="button"
              className="button button--secondary button--bordered"
              onClick={handleRequestW9InfoClick}
            >
              Request W9 Information
            </button>


            <button
              type="button"
              className="button button--secondary button--bordered"
              // onClick={handleAskQuestionClick}
              onClick={handleAskQuestionGorgiasClick}
            >
              Ask a Question
            </button>

            {!order.hasInstallation && order.lineItemsSubtotalPrice > 1500 && (
              <button
                type="button"
                className="button button--primary button--bordered"
                onClick={handleRequestInstallationClick}
              >
                Request Installation Quote
              </button>
            )}

            {isGovernmentPoOrder(order) && !hasPurchaseOrderFile(order) && (
              <button
                type="button"
                className="button button--secondary button--bordered"
                onClick={handleUploadPoClick}
              >
                Upload Purchase Order
              </button>
            )}


            {hasOutstanding && (
              <button
                type="button"
                className="button button--blue button--bordered"
                onClick={ openAchInfoPdf }
              >
                View ACH Information
              </button>
            )}


            {/* {order.statusPageUrl && (
              <a className="button button--primary button--bordered" href={order.statusPageUrl} target="_blank" rel="noopener noreferrer">
                Track Shipments
              </a>
            )} */}

        </div>


        {/* {hasOutstanding && (
          <div className="order-note">
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); openAchInfoPdf(); }}
            >View ACH Information <PdfIcon strokeWidth={4} /></a>
          </div>
        )} */}

        

        {/*
          Pre-install paperwork, rendered inline rather than as a second React
          root. It receives the order we've already fetched, so it doesn't need
          to resolve the id from the hash - which is what broke when this lived
          in its own bundle: it read the hash once at DOMContentLoaded, before
          the router had navigated to /order/:id.

          {order.hasInstallation && order.showInstallPortal && (
        */}
        {order.hasInstallation && (
          <InstallForm orderId={orderId} order={order} />
        )}

      </div>

      {modalContent && (
        <ModalMain onClose={closeModal} className={modalContent.props?.className}>
          {modalContent}
        </ModalMain>
      )}
    </main>
  );
}

export default OrderDetailPage;
